import { GamesSkillGameDto, gamesCli } from 'types/swagger';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';
import { convertInCurrencyValue, purgeNulls, skipStaticPageGeneration } from 'utility/functions';

import { GameDetailSpecProps } from 'components/gameDetail/gameDetailSpec/GameDetailSpec';
import { Layout } from 'components/Layout';
import PageContent from 'components/page-content/slot-gioco-slug/PageContent';
import { PagesPath } from 'types/pages';
import { Seo } from 'components/seo';
import { cleanConfig } from 'lib/datoCms/utils';
import { generateSeoMetaTags } from 'components/seo';
import { getAllMightLikes } from 'lib/datoCms/queries/getAllMightLikes';
import { getDatoCMSData } from 'sites/snai/lib/datoCms/queries/getDatoCMSData';
import { ssrHeaders } from 'lib/auth/ssr';
import { ISR_REVALIDATE } from 'utility/constant';
import { getCardsFromSearchField } from 'lib/ssr/search';
import { Category } from 'types/categoryEnum';
import { getSlotTopPage } from 'lib/datoCms/queries/getSlotTopPage';

const SlotSlug = ({
  slot,
  site,
  header,
  footer,
  needHelp,
  canonical,
  seoMetaTags,
  sidebarInstantGame,
  allMightLikes,
  choseFromSnaiCardProps,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  return (
    <Layout
      header={header}
      needHelp={needHelp}
      footer={footer}
      mightLikes={allMightLikes}
      sidebarInstantGame={sidebarInstantGame}
    >
      <Seo siteSeo={site} metaTags={seoMetaTags} canonicalUrl={canonical} />
      <PageContent slot={slot} choseFromSnaiCardsSearch={choseFromSnaiCardProps} />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const paths: Array<PagesPath> = [];

  if (!skipStaticPageGeneration()) {
    const apiParams = await ssrHeaders();
    const { ok, data } = await gamesCli.externalgames.slotCatalogList(apiParams);

    if (ok) {
      const { games } = data ?? {};
      games?.forEach((game: GamesSkillGameDto) => {
        locales?.forEach((locale) => {
          paths.push({ params: { slug: encodeURIComponent(`${game.slug}`) }, locale });
        });
      });
    }
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  try {
    const { slug } = params as { slug: string };

    console.log(`## slot-top.Gioco.getStaticProps ${slug}`);

    const prParams = ssrHeaders(locale);
    const prCmsData = getDatoCMSData('common', locale);
    const prPageData = getSlotTopPage(locale);

    const [apiParams, cmsData, pageData] = await Promise.all([prParams, prCmsData, prPageData]);

    const { site, header, footer, needHelp, allLabels: labels, sidebarInstantGame, config } = cmsData ?? {};
    const {
      slotTopPage: { searchField },
    } = pageData ?? {};

    const prCatalog = gamesCli.externalgames.slotCatalogDetail(slug, apiParams);
    const prMightLikes = getAllMightLikes(locale);
    const cardsPr = getCardsFromSearchField(searchField ?? [], Category.Slot);
    const [{ data, error }, mightLikes, cards] = await Promise.all([prCatalog, prMightLikes, cardsPr]);

    if (!data) {
      console.log(`ERROR: "/externalgames/slot/catalog/${slug}" is empty`, error);
      return { notFound: true };
    }

    const {
      image,
      isNew,
      isExclusive,
      description,
      functionality,
      lines,
      volatility,
      tema,
      title,
      produttore,
      idProduttore,
      jackpot,
      playInfo,
      codicePiattaforma,
      isInstant,
      promo,
      hasDemo,
      gameMode,
      modalita,
    } = data ?? {};

    const specs: GameDetailSpecProps[] = [
      {
        label: 'tema',
        values: tema ? [...tema] : null,
      },
      {
        label: 'produttore',
        values: produttore ? [produttore] : null,
      },
      {
        label: 'jackpot',
        values: jackpot ? [String(convertInCurrencyValue(jackpot / 100))] : null,
      },
      {
        label: 'volatility',
        values: volatility?.label ? [volatility.label] : null,
      },
      {
        label: 'functionality',
        values: functionality ? [...functionality] : null,
      },
      {
        label: 'lines',
        values: lines ? [lines] : null,
      },
      {
        label: 'gamemode',
        values: gameMode ? [...gameMode] : null,
      },
    ];

    const seoMetaTags = generateSeoMetaTags(`Gioca alla slot Machine ${title}`, description);

    const imgAlt = Reflect.get(image ?? {}, 'alt');
    const imgSrc = Reflect.get(image ?? {}, 'srcCover');
    const imgTitle = Reflect.get(image ?? {}, 'title');

    return {
      props: {
        site,
        slot: purgeNulls({
          title,
          idProduttore,
          coverImage: image
            ? {
                src: imgSrc ?? '',
                alt: imgAlt ?? imgTitle ?? '',
              }
            : undefined,
          isNew,
          isExclusive,
          description,
          specs,
          playInfo,
          codicePiattaforma,
          isInstant,
          promo,
          hasDemo,
          modalita,
        }),
        footer,
        labels,
        config: cleanConfig(config),
        header,
        needHelp,
        canonical: '',
        seoMetaTags,
        allMightLikes: mightLikes?.allMightLikes ?? null,
        sidebarInstantGame,
        choseFromSnaiCardProps: cards ?? null,
      },
      revalidate: ISR_REVALIDATE,
    };
  } catch (err: any) {
    return { props: { errors: err.message } };
  }
};

export default SlotSlug;
